import { BasemapProps, fetchToken, TopicLayerSettings } from '@agissc/ktag-map';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import config from '../../../utils/AppConfig';

import { SubLayerProps } from '../../mapContent/api/Contracts';

export interface MapDefinition {
  basemap: BasemapProps;
  topicLayers: string[];
  topicLayerSettings: Record<string, TopicLayerSettings>;
  topicSubLayerSetting: Record<string, SubLayerProps[]>;
}

export interface UserMapConfiguration {
  id: string;
  name: string;
  mapDefinitionValue: MapDefinition;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
}

const fetchUserMapConfigurations = async () => {
  const headers = new Headers();
  const token = await fetchToken();
  headers.set('Authorization', `Bearer ${token}`);

  const url = `${config.apis.userMapDefinitionsLocation}/self`;
  const requestApi = new Request(url);
  const response = await fetch(requestApi, {
    method: 'GET',
    headers,
  });

  const result = (await response.json()) as UserMapConfiguration[];
  return result;
};

const addUserMapConfiguration = async (userMapDefinition: UserMapConfiguration) => {
  const token = await fetchToken();
  const url = `${config.apis.userMapDefinitionsLocation}/self`;

  const requestApi = new Request(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(userMapDefinition),
  });
  return fetch(requestApi);
};

const updateUserMapConfiguration = async (id: string, newName: string) => {
  const token = await fetchToken();
  const url = `${config.apis.userMapDefinitionsLocation}/self/${id}`;
  const requestApi = new Request(url, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(newName),
  });
  const response = await fetch(requestApi);
  return response;
};

const updateOrderUserMapConfiguration = async (ids: string[]) => {
  const token = await fetchToken();
  const url = `${config.apis.userMapDefinitionsLocation}/self/order`;
  const requestApi = new Request(url, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(ids),
  });
  const response = await fetch(requestApi);
  return response;
};

const removeUserMapDefinition = async (id: string) => {
  const token = await fetchToken();

  const url = `${config.apis.userMapDefinitionsLocation}/${id}`;
  const requestApi = new Request(url, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  const response = await fetch(requestApi);
  return response;
};

export const useUserMapConfigurations = () => {
  return useQuery<UserMapConfiguration[]>({
    queryKey: ['userMapDefinitions'],
    queryFn: () => fetchUserMapConfigurations(),
    gcTime: 3600000,
    staleTime: Infinity,
  });
};

export const useAddUserMapConfiguration = () => {
  const queryClient = useQueryClient();

  return useMutation<Response, Error, UserMapConfiguration>({
    mutationFn: addUserMapConfiguration,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userMapDefinitions'] });
    },
  });
};

export const useUpdateUserMapConfiguration = () => {
  const queryClient = useQueryClient();

  return useMutation<Response, Error, { id: string; newName: string }>({
    mutationFn: ({ id, newName }) => updateUserMapConfiguration(id, newName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userMapDefinitions'] });
    },
  });
};

export const useRemoveUserMapConfiguration = () => {
  const queryClient = useQueryClient();

  return useMutation<Response, Error, string>({
    mutationFn: removeUserMapDefinition,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userMapDefinitions'] });
    },
  });
};

export const useUpdateOrderUserMapConfiguration = () => {
  const queryClient = useQueryClient();

  return useMutation<Response, Error, string[]>({
    mutationFn: updateOrderUserMapConfiguration,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userMapDefinitions'] });
    },
  });
};
